import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import cloneDeep from "lodash/cloneDeep";
import set from "lodash/set";
import { toast } from "sonner";
import {
  Loader2, LogOut, Save, LayoutDashboard, FileText, Users, ExternalLink,
  Plus, Trash2, Globe, MapPin, Building2, TrendingUp, Eye, Mail, Upload, Check, RotateCcw,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { api } from "../lib/api";
import { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const TABS = [
  { id: "analytics", label: "Analytics", icon: LayoutDashboard },
  { id: "content", label: "Content", icon: FileText },
  { id: "leads", label: "Leads", icon: Users },
];

const inputCls =
  "w-full bg-white border border-gold-subtle rounded-md px-3 py-2.5 text-navy text-sm font-body outline-none focus:border-barn focus:ring-1 focus:ring-barn/30 transition-[border-color,box-shadow]";
const labelCls = "font-body text-[11px] font-semibold uppercase tracking-wider text-navy-soft mb-1.5 block";

function Field({ label, value, onChange, textarea, rows = 3, placeholder }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {textarea ? (
        <textarea className={`${inputCls} resize-none`} rows={rows} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
      ) : (
        <input className={inputCls} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
      )}
    </div>
  );
}

function ImageField({ label, value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = data.url?.startsWith("http") ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      onChange(url);
      toast.success("Image uploaded — remember to Save.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div>
      <label className={labelCls}>{label}</label>
      <div className="flex gap-3 items-start">
        {value ? <img src={value} alt="" className="w-16 h-16 object-cover rounded-md border border-gold-subtle shrink-0" /> : <div className="w-16 h-16 rounded-md border border-dashed border-gold-subtle shrink-0" />}
        <div className="flex-1 space-y-2">
          <input className={inputCls} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="Paste image URL or upload →" />
          <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid={`upload-input-${(label || "img").toLowerCase().replace(/\s/g, "-")}`} />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid={`upload-btn-${(label || "img").toLowerCase().replace(/\s/g, "-")}`}
            className="font-body text-xs font-semibold px-3 py-2 border border-gold-subtle rounded-md text-navy hover:border-barn hover:text-barn transition-colors flex items-center gap-1.5 disabled:opacity-60"
          >
            {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
            {uploading ? "Uploading…" : "Upload image"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-gold-subtle rounded-lg bg-white p-6 mb-5 shadow-[0_10px_30px_-25px_rgba(26,37,48,0.3)]">
      <h3 className="font-display text-xl text-navy mb-5">{title}</h3>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="border border-gold-subtle rounded-lg bg-white p-5">
      <div className="flex items-center gap-2 text-barn mb-3"><Icon size={15} /><span className="font-body text-[11px] font-semibold uppercase tracking-wider text-navy-soft">{label}</span></div>
      <div className="font-display text-4xl text-navy">{value}</div>
      {sub && <div className="font-body text-[12px] text-ink mt-1">{sub}</div>}
    </div>
  );
}

function TopList({ title, icon: Icon, items }) {
  const max = Math.max(...(items?.map((i) => i.count) || [1]), 1);
  return (
    <div className="border border-gold-subtle rounded-lg bg-white p-5">
      <div className="flex items-center gap-2 text-navy mb-4"><Icon size={15} className="text-barn" /><span className="font-body text-[12px] font-semibold uppercase tracking-wider">{title}</span></div>
      <div className="space-y-3">
        {(!items || items.length === 0) && <div className="font-body text-sm text-ink">No data yet.</div>}
        {items?.map((it) => (
          <div key={it.name}>
            <div className="flex justify-between text-sm mb-1"><span className="text-navy font-body truncate pr-2">{it.name}</span><span className="text-ink font-body text-xs">{it.count}</span></div>
            <div className="h-1.5 bg-cream rounded-full overflow-hidden"><div className="h-full bg-barn rounded-full" style={{ width: `${(it.count / max) * 100}%` }} /></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Analytics() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/analytics").then(({ data }) => setData(data)).catch(() => toast.error("Failed to load analytics")); }, []);
  if (!data) return <div className="flex items-center justify-center py-32 text-ink"><Loader2 className="animate-spin" /></div>;
  const t = data.totals;
  return (
    <div className="space-y-6" data-testid="analytics-panel">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Eye} label="Visits · Today" value={t.visits_today} sub={`${t.visits_all} all-time`} />
        <StatCard icon={TrendingUp} label="Visits · 7 Days" value={t.visits_7d} sub={`${t.visits_30d} in 30 days`} />
        <StatCard icon={Mail} label="Leads · 30 Days" value={t.leads_30d} sub={`${t.leads_all} all-time`} />
        <StatCard icon={TrendingUp} label="Conversion" value={`${t.conversion}%`} sub="leads / visits" />
      </div>

      <div className="border border-gold-subtle rounded-lg bg-white p-5">
        <div className="font-body text-[12px] font-semibold uppercase tracking-wider text-navy-soft mb-5">Visits & Leads — Last 30 Days</div>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={data.timeseries} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="gv" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#B23A2E" stopOpacity={0.45} /><stop offset="100%" stopColor="#B23A2E" stopOpacity={0} /></linearGradient>
              <linearGradient id="gl" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#3E5C8A" stopOpacity={0.45} /><stop offset="100%" stopColor="#3E5C8A" stopOpacity={0} /></linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#E4DFD3" vertical={false} />
            <XAxis dataKey="date" tick={{ fill: "#4A5568", fontSize: 10 }} tickFormatter={(d) => d.slice(5)} interval={4} axisLine={{ stroke: "#E4DFD3" }} tickLine={false} />
            <YAxis tick={{ fill: "#4A5568", fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E4DFD3", borderRadius: 8, fontSize: 12 }} labelStyle={{ color: "#4A5568" }} />
            <Area type="monotone" dataKey="visits" stroke="#B23A2E" fill="url(#gv)" strokeWidth={2} name="Visits" />
            <Area type="monotone" dataKey="leads" stroke="#3E5C8A" fill="url(#gl)" strokeWidth={2} name="Leads" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <TopList title="By Country" icon={Globe} items={data.by_country} />
        <TopList title="By State / Region" icon={MapPin} items={data.by_region} />
        <TopList title="By City" icon={Building2} items={data.by_city} />
      </div>
    </div>
  );
}

function Leads() {
  const [leads, setLeads] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => { api.get("/leads").then(({ data }) => setLeads(data)).catch(() => toast.error("Failed to load leads")); }, []);

  const toggleRead = async (lead) => {
    setBusy(lead.id);
    try {
      await api.patch(`/leads/${lead.id}`, { read: !lead.read });
      setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, read: !lead.read } : l)));
    } catch { toast.error("Could not update lead."); }
    finally { setBusy(null); }
  };

  const remove = async (lead) => {
    if (!window.confirm(`Delete lead from ${lead.name}? This cannot be undone.`)) return;
    setBusy(lead.id);
    try {
      await api.delete(`/leads/${lead.id}`);
      setLeads((ls) => ls.filter((l) => l.id !== lead.id));
      toast.success("Lead deleted.");
    } catch { toast.error("Could not delete lead."); }
    finally { setBusy(null); }
  };

  if (!leads) return <div className="flex items-center justify-center py-32 text-ink"><Loader2 className="animate-spin" /></div>;
  if (leads.length === 0) return <div className="border border-gold-subtle rounded-lg bg-white p-16 text-center font-body text-ink" data-testid="leads-panel">No leads yet. Submissions from the contact form appear here.</div>;

  const unread = leads.filter((l) => !l.read).length;

  return (
    <div className="space-y-3" data-testid="leads-panel">
      <div className="font-body text-sm text-ink mb-1">{leads.length} total · <span className="text-barn font-semibold">{unread} unread</span></div>
      {leads.map((l) => (
        <div key={l.id} className={`border rounded-lg p-5 transition-colors ${l.read ? "border-gold-subtle bg-white" : "border-barn/30 bg-barn/[0.03]"}`} data-testid={`lead-row-${l.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="font-display text-lg text-navy flex items-center gap-2">
                {!l.read && <span className="text-[9px] font-body font-bold uppercase tracking-wider bg-barn text-white px-1.5 py-0.5 rounded" data-testid={`lead-new-${l.id}`}>New</span>}
                {l.name} <span className="text-ink font-body text-sm">· {l.company || "—"}</span>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 font-body text-[12px] text-ink">
                <a href={`mailto:${l.email}`} className="hover:text-barn">{l.email}</a>
                {l.phone && <span>{l.phone}</span>}
                <span className="text-barn font-semibold">{l.service || "General"}</span>
              </div>
            </div>
            <div className="text-right font-body text-[11px] text-ink">
              <div>{new Date(l.created_at).toLocaleString()}</div>
              <div className="mt-1">{[l.city, l.region, l.country].filter((x) => x && x !== "Unknown").join(", ") || "—"}</div>
            </div>
          </div>
          <p className="mt-3 font-body text-sm text-navy leading-relaxed border-t border-gold-subtle pt-3 whitespace-pre-wrap">{l.message}</p>
          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={() => toggleRead(l)}
              disabled={busy === l.id}
              data-testid={`lead-toggle-read-${l.id}`}
              className="font-body text-xs font-semibold px-3 py-1.5 rounded-md border border-gold-subtle text-navy hover:border-navy transition-colors flex items-center gap-1.5 disabled:opacity-60"
            >
              {l.read ? <><RotateCcw size={13} /> Mark unread</> : <><Check size={13} /> Mark as read</>}
            </button>
            <button
              onClick={() => remove(l)}
              disabled={busy === l.id}
              data-testid={`lead-delete-${l.id}`}
              className="font-body text-xs font-semibold px-3 py-1.5 rounded-md border border-gold-subtle text-barn hover:border-barn hover:bg-barn hover:text-white transition-colors flex items-center gap-1.5 disabled:opacity-60"
            >
              <Trash2 size={13} /> Delete
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function get(obj, path) {
  return path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);
}

function ContentEditor() {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/content").then(({ data }) => setContent(data)); }, []);

  const upd = useCallback((path, value) => {
    setContent((prev) => { const next = cloneDeep(prev); set(next, path, value); return next; });
  }, []);

  const save = async () => {
    setSaving(true);
    try { await api.put("/content", { content }); toast.success("Site content updated & published."); }
    catch { toast.error("Failed to save."); }
    finally { setSaving(false); }
  };

  if (!content) return <div className="flex items-center justify-center py-32 text-ink"><Loader2 className="animate-spin" /></div>;

  const c = content;
  const arr = (path, def) => (Array.isArray(get(c, path)) ? get(c, path) : def);

  const SaveBtn = () => (
    <button onClick={save} disabled={saving} data-testid="content-save-btn" className="font-body text-sm font-semibold px-6 py-3 bg-barn text-ivory rounded-md hover:bg-[#9c3227] transition-colors flex items-center gap-2 disabled:opacity-60">
      {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save & Publish
    </button>
  );

  return (
    <div data-testid="content-panel">
      <div className="flex justify-between items-center mb-5 sticky top-[57px] z-10 bg-ivory/95 backdrop-blur py-3">
        <p className="font-body text-sm text-ink hidden sm:block">Edit any text or image. Changes publish live on save.</p>
        <SaveBtn />
      </div>

      <Section title="Brand">
        <Field label="Company Name" value={c.brand?.name} onChange={(v) => upd("brand.name", v)} />
        <Field label="Tagline" value={c.brand?.tagline} onChange={(v) => upd("brand.tagline", v)} />
        <ImageField label="Logo" value={c.brand?.logoUrl} onChange={(v) => upd("brand.logoUrl", v)} />
      </Section>

      <Section title="Hero">
        <Field label="Overline" value={c.hero?.overline} onChange={(v) => upd("hero.overline", v)} />
        <div>
          <label className={labelCls}>Title Lines (last line shown italic in red)</label>
          {arr("hero.titleLines", []).map((line, i) => (
            <div key={i} className="flex gap-2 mb-2">
              <input className={inputCls} value={line} onChange={(e) => { const a = [...c.hero.titleLines]; a[i] = e.target.value; upd("hero.titleLines", a); }} />
              <button onClick={() => upd("hero.titleLines", c.hero.titleLines.filter((_, x) => x !== i))} className="text-ink hover:text-barn px-2"><Trash2 size={16} /></button>
            </div>
          ))}
          <button onClick={() => upd("hero.titleLines", [...arr("hero.titleLines", []), "New line"])} className="font-body text-[12px] font-semibold text-barn flex items-center gap-1"><Plus size={13} /> Add line</button>
        </div>
        <Field label="Subtitle" textarea value={c.hero?.subtitle} onChange={(v) => upd("hero.subtitle", v)} />
        <div className="grid grid-cols-2 gap-4">
          <Field label="Primary CTA" value={c.hero?.ctaPrimary} onChange={(v) => upd("hero.ctaPrimary", v)} />
          <Field label="Secondary CTA" value={c.hero?.ctaSecondary} onChange={(v) => upd("hero.ctaSecondary", v)} />
        </div>
        <ImageField label="Hero Image" value={c.hero?.backgroundImage} onChange={(v) => upd("hero.backgroundImage", v)} />
      </Section>

      <Section title="Marquee">
        <Field label="Marquee Text (separate items with —)" value={c.marqueeText} onChange={(v) => upd("marqueeText", v)} />
      </Section>

      <Section title="Stats">
        {arr("stats", []).map((s, i) => (
          <div key={i} className="grid grid-cols-[1fr_2fr_auto] gap-2 items-end">
            <Field label="Value" value={s.value} onChange={(v) => { const a = cloneDeep(c.stats); a[i].value = v; upd("stats", a); }} />
            <Field label="Label" value={s.label} onChange={(v) => { const a = cloneDeep(c.stats); a[i].label = v; upd("stats", a); }} />
            <button onClick={() => upd("stats", c.stats.filter((_, x) => x !== i))} className="text-ink hover:text-barn pb-3"><Trash2 size={16} /></button>
          </div>
        ))}
        <button onClick={() => upd("stats", [...arr("stats", []), { value: "0", label: "New stat" }])} className="font-body text-[12px] font-semibold text-barn flex items-center gap-1"><Plus size={13} /> Add stat</button>
      </Section>

      <Section title="About / The Edge">
        <Field label="Overline" value={c.about?.overline} onChange={(v) => upd("about.overline", v)} />
        <Field label="Heading" textarea rows={2} value={c.about?.heading} onChange={(v) => upd("about.heading", v)} />
        {arr("about.paragraphs", []).map((p, i) => (
          <div key={i} className="flex gap-2 items-start">
            <textarea className={`${inputCls} resize-none`} rows={2} value={p} onChange={(e) => { const a = [...c.about.paragraphs]; a[i] = e.target.value; upd("about.paragraphs", a); }} />
            <button onClick={() => upd("about.paragraphs", c.about.paragraphs.filter((_, x) => x !== i))} className="text-ink hover:text-barn pt-2"><Trash2 size={16} /></button>
          </div>
        ))}
        <button onClick={() => upd("about.paragraphs", [...arr("about.paragraphs", []), ""])} className="font-body text-[12px] font-semibold text-barn flex items-center gap-1"><Plus size={13} /> Add paragraph</button>
        <Field label="Credentials (comma separated)" value={arr("about.credentials", []).join(", ")} onChange={(v) => upd("about.credentials", v.split(",").map((x) => x.trim()).filter(Boolean))} />
        <ImageField label="About Image" value={c.about?.image} onChange={(v) => upd("about.image", v)} />
      </Section>

      <Section title="Services (Capabilities)">
        {arr("services", []).map((s, i) => (
          <div key={s.id || i} className="border border-gold-subtle rounded-md p-4 space-y-3 bg-cream">
            <div className="flex justify-between items-center">
              <span className="font-body text-xs font-semibold text-barn">{s.number} · {s.title}</span>
              <button onClick={() => upd("services", c.services.filter((_, x) => x !== i))} className="text-ink hover:text-barn"><Trash2 size={15} /></button>
            </div>
            <div className="grid grid-cols-[80px_1fr] gap-2">
              <Field label="No." value={s.number} onChange={(v) => { const a = cloneDeep(c.services); a[i].number = v; upd("services", a); }} />
              <Field label="Title" value={s.title} onChange={(v) => { const a = cloneDeep(c.services); a[i].title = v; upd("services", a); }} />
            </div>
            <Field label="Description" textarea value={s.description} onChange={(v) => { const a = cloneDeep(c.services); a[i].description = v; upd("services", a); }} />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={labelCls}>Size</label>
                <select className={inputCls} value={s.span || "normal"} onChange={(e) => { const a = cloneDeep(c.services); a[i].span = e.target.value; upd("services", a); }}>
                  <option value="normal">Normal</option>
                  <option value="wide">Wide (2 cols)</option>
                </select>
              </div>
              <Field label="Tags (comma sep)" value={(s.tags || []).join(", ")} onChange={(v) => { const a = cloneDeep(c.services); a[i].tags = v.split(",").map((x) => x.trim()).filter(Boolean); upd("services", a); }} />
            </div>
          </div>
        ))}
        <button onClick={() => upd("services", [...arr("services", []), { id: `svc-${Date.now()}`, number: String(arr("services", []).length + 1).padStart(2, "0"), title: "New Service", description: "", span: "normal", tags: [] }])} className="font-body text-[12px] font-semibold text-barn flex items-center gap-1"><Plus size={13} /> Add service</button>
      </Section>

      <Section title="Why Now (Manifesto)">
        <Field label="Overline" value={c.manifesto?.overline} onChange={(v) => upd("manifesto.overline", v)} />
        <Field label="Heading" value={c.manifesto?.heading} onChange={(v) => upd("manifesto.heading", v)} />
        {arr("manifesto.chapters", []).map((ch, i) => (
          <div key={i} className="border border-gold-subtle rounded-md p-4 space-y-3 bg-cream">
            <div className="flex justify-between items-center"><span className="font-body text-xs font-semibold text-barn">{ch.number}</span><button onClick={() => upd("manifesto.chapters", c.manifesto.chapters.filter((_, x) => x !== i))} className="text-ink hover:text-barn"><Trash2 size={15} /></button></div>
            <div className="grid grid-cols-[80px_1fr] gap-2">
              <Field label="No." value={ch.number} onChange={(v) => { const a = cloneDeep(c.manifesto.chapters); a[i].number = v; upd("manifesto.chapters", a); }} />
              <Field label="Title" value={ch.title} onChange={(v) => { const a = cloneDeep(c.manifesto.chapters); a[i].title = v; upd("manifesto.chapters", a); }} />
            </div>
            <Field label="Body" textarea value={ch.body} onChange={(v) => { const a = cloneDeep(c.manifesto.chapters); a[i].body = v; upd("manifesto.chapters", a); }} />
          </div>
        ))}
        <button onClick={() => upd("manifesto.chapters", [...arr("manifesto.chapters", []), { number: String(arr("manifesto.chapters", []).length + 1).padStart(2, "0"), title: "New chapter", body: "" }])} className="font-body text-[12px] font-semibold text-barn flex items-center gap-1"><Plus size={13} /> Add chapter</button>
      </Section>

      <Section title="Contact Info">
        <Field label="Overline" value={c.contact?.overline} onChange={(v) => upd("contact.overline", v)} />
        <Field label="Heading" value={c.contact?.heading} onChange={(v) => upd("contact.heading", v)} />
        <Field label="Subtitle" textarea value={c.contact?.subtitle} onChange={(v) => upd("contact.subtitle", v)} />
        <div className="grid grid-cols-2 gap-4">
          <Field label="Email" value={c.contact?.email} onChange={(v) => upd("contact.email", v)} />
          <Field label="Phone" value={c.contact?.phone} onChange={(v) => upd("contact.phone", v)} />
          <Field label="WhatsApp (digits only, incl. country code)" value={c.contact?.whatsapp} onChange={(v) => upd("contact.whatsapp", v)} />
          <Field label="Hours" value={c.contact?.hours} onChange={(v) => upd("contact.hours", v)} />
        </div>
        <Field label="Address" value={c.contact?.address} onChange={(v) => upd("contact.address", v)} />
        <ImageField label="Contact Image (Fort Worth skyline)" value={c.contact?.backgroundImage} onChange={(v) => upd("contact.backgroundImage", v)} />
      </Section>

      <Section title="Footer">
        <Field label="Blurb" textarea value={c.footer?.blurb} onChange={(v) => upd("footer.blurb", v)} />
        <Field label="Copyright" value={c.footer?.copyright} onChange={(v) => upd("footer.copyright", v)} />
      </Section>

      <div className="flex justify-end pb-10"><SaveBtn /></div>
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("analytics");

  return (
    <div className="min-h-screen bg-ivory">
      <div className="paper-grain" />
      <header className="border-b border-gold-subtle sticky top-0 z-30 glass-light">
        <div className="max-w-6xl mx-auto px-4 md:px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <span className="font-display text-lg text-navy">Lieber <span className="font-body text-[10px] text-gold uppercase tracking-widest ml-1">Admin</span></span>
            <nav className="hidden sm:flex gap-1">
              {TABS.map((tb) => (
                <button key={tb.id} onClick={() => setTab(tb.id)} data-testid={`admin-tab-${tb.id}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md font-body text-sm font-medium transition-colors ${tab === tb.id ? "bg-barn text-ivory" : "text-navy/70 hover:text-navy hover:bg-cream"}`}>
                  <tb.icon size={15} /> {tb.label}
                </button>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" target="_blank" rel="noreferrer" className="text-navy/70 hover:text-barn flex items-center gap-1 font-body text-sm"><ExternalLink size={14} /> <span className="hidden sm:inline">View site</span></a>
            <button onClick={() => { logout(); navigate("/login"); }} data-testid="admin-logout" className="text-navy/70 hover:text-barn flex items-center gap-1 font-body text-sm"><LogOut size={14} /> <span className="hidden sm:inline">Logout</span></button>
          </div>
        </div>
        <div className="sm:hidden flex border-t border-gold-subtle">
          {TABS.map((tb) => (
            <button key={tb.id} onClick={() => setTab(tb.id)} className={`flex-1 flex items-center justify-center gap-1.5 py-3 font-body text-xs font-semibold ${tab === tb.id ? "text-barn border-b-2 border-barn" : "text-navy/60"}`}><tb.icon size={14} /> {tb.label}</button>
          ))}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-5 py-8 relative">
        <div className="mb-6">
          <h1 className="font-display text-3xl md:text-4xl text-navy capitalize">{tab}</h1>
          <p className="font-body text-sm text-ink mt-1">Signed in as {user?.email}</p>
        </div>
        {tab === "analytics" && <Analytics />}
        {tab === "content" && <ContentEditor />}
        {tab === "leads" && <Leads />}
      </main>
    </div>
  );
}
